import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const MemberAuthContext = createContext(null);
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

export function MemberAuthProvider({ children }) {
  const [member, setMember] = useState(null); // null = checking, false = signed out, object = signed in
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/member/auth/me");
      setMember(data);
    } catch {
      setMember(false);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    const isGoogleCallback = window.location.pathname === "/account" && new URLSearchParams(window.location.search).get("code");
    if (isGoogleCallback) {
      setReady(true);
      return;
    }
    refresh();
  }, [refresh]);

  const register = async (payload) => {
    const { data } = await api.post("/member/auth/register", payload);
    setMember(data.member);
    return data.member;
  };

  const login = async (email, password) => {
    const { data } = await api.post("/member/auth/login", { email, password });
    setMember(data.member);
    return data.member;
  };

  const loginWithGoogle = () => {
    if (!GOOGLE_CLIENT_ID) {
      toast.error("Google sign-in is not configured yet.");
      return;
    }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUri = `${window.location.origin}/account`;
    const state = window.crypto.randomUUID();
    sessionStorage.setItem("google_oauth_state", state);
    const params = new URLSearchParams({
      client_id: GOOGLE_CLIENT_ID,
      redirect_uri: redirectUri,
      response_type: "code",
      scope: "openid email profile",
      access_type: "online",
      prompt: "select_account",
      state,
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };

  const completeGoogleLogin = async (code) => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUri = `${window.location.origin}/account`;
    const { data } = await api.post("/member/auth/google/callback", { code, redirect_uri: redirectUri });
    setMember(data.member);
    return data.member;
  };

  const logout = async () => {
    try {
      await api.post("/member/auth/logout");
    } catch (e) {
      console.error("Member logout failed:", e);
    }
    setMember(false);
  };

  return (
    <MemberAuthContext.Provider value={{ member, ready, register, login, loginWithGoogle, completeGoogleLogin, logout, refresh, setMember }}>
      {children}
    </MemberAuthContext.Provider>
  );
}

export const useMemberAuth = () => useContext(MemberAuthContext);
